import React, { useEffect, useState } from "react";
import { Calendar, Upload, X, Loader } from "lucide-react";
import { useAlert } from "components/UI_components/Alert";
import { BASE_URL } from "utils/constant";
import { resolveTransactionFeedback } from "utils/transactionFeedback";

export default function OtherPaymentForm({ onClose }) {
  const [transactionType, setTransactionType] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [remarks, setRemarks] = useState("");
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false); // New state to track if already saved
  const [otherTransactionCatagory, setOtherTransactionCatagory] =
    useState(null);
  const { addAlert } = useAlert();
  // Get current date for date picker default
  const currentDate = new Date();
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const formattedCurrentDate = `${currentDate.getFullYear()}-${(
    currentDate.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}-${currentDate.getDate().toString().padStart(2, "0")}`;

  const [transactionDate, setTransactionDate] = useState(formattedCurrentDate);

  const transactionTypes = [
    "Equipments",
    "Electricity",
    "Transport",
    "Municipality/Panchayat",
    "Water Bill",
  ];

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const removeImage = () => {
    setImage(null);
    setPreviewUrl(null);
  };

  const toggleDatePicker = () => {
    setShowDatePicker(!showDatePicker);
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setTransactionDate(newDate);
    setShowDatePicker(false);
  };

  const handleNowButtonClick = () => {
    setTransactionDate(formattedCurrentDate);
    setShowDatePicker(false);
  };

  const handleSave = async () => {
    // Prevent multiple submissions
    if (!transactionType || !amount || isLoading || isSaved) return;

    setIsLoading(true);
    setIsSaved(true); // Mark as saved to prevent future clicks

    const site = localStorage.getItem("site");

    const formData = new FormData();
    formData.append("transaction_type", transactionType);
    formData.append("transaction_date", transactionDate);
    formData.append("amount", parseInt(amount));
    formData.append("transaction_method", paymentMethod);
    formData.append("comments", remarks);
    formData.append("transaction_catagory_id", otherTransactionCatagory);
    formData.append("site", parseInt(site));

    if (image) {
      formData.append("ireceipt_image", image);
    }

    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/finance/create-general-transaction/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        },
      );
      const data = await response.json();
      const feedback = resolveTransactionFeedback(data, {
        successMessage: "Payment created successfully",
        errorMessage: "Failed to create payment",
      });

      if (feedback.success) {
        if (feedback.approvalRequired) {
          addAlert(feedback.message, feedback.alertType);
        }
        onClose();
      } else {
        addAlert(feedback.message, feedback.alertType);
        // If API fails, allow user to try again
        setIsSaved(false);
      }
    } catch (error) {
      console.error("Error creating payment:", error);
      addAlert(error.message || "Failed to create payment", "error");
      // If there's an error, allow user to try again
      setIsSaved(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchTransactionCatagories = async () => {
      try {
        const accessToken = localStorage.getItem("access");
        const response = await fetch(
          `${BASE_URL}/finance/get-all-finance-catagory/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );
        if (response.ok) {
          const res = await response.json();
          const data = res.data;
          const purchaseCatagory = data.find(
            (cat) => cat.catagory_name === "General Transaction",
          );
          if (purchaseCatagory) {
            setOtherTransactionCatagory(purchaseCatagory.id);
          }
        } else {
          console.error("Failed to fetch material transaction catagories");
        }
      } catch (error) {
        console.error("Error fetching material transaction catagories:", error);
      }
    };
    fetchTransactionCatagories();
  }, []);

  // Check if save button should be disabled
  const isSaveDisabled = !transactionType || !amount || isLoading || isSaved;

  return (
    <div className="w-full p-6 bg-gray-50">
      {/* Header */}
      <div className="flex justify-between items-center mb-2 bg-white px-4 py-3 rounded-lg shadow-sm">
        <button onClick={onClose}>
          <X className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-semibold">General Payment</h1>
        <div className="w-5" />
      </div>

      <div className="flex flex-col h-[73vh]" style={{ overflowY: "auto" }}>
        {/* Main Form */}
        <div className="grid grid-cols-1 gap-4">
          {/* Purpose of Transaction */}
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <label className="block text-sm text-gray-600 mb-1">
              Purpose of Transaction
            </label>
            <input
              type="text"
              className="w-full p-3 bg-white rounded-lg border border-gray-200 mb-3"
              placeholder="Type"
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
              disabled={isSaved} // Disable form inputs after save
            />
            <div className="flex flex-wrap gap-2">
              {transactionTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => setTransactionType(type)}
                  disabled={isSaved} // Disable buttons after save
                  className={`px-4 py-2 rounded-full text-sm ${
                    transactionType === type
                      ? "bg-purple-100 text-purple-600"
                      : "bg-gray-100 text-gray-600"
                  } ${isSaved ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Left and Right Side Sections */}
          <div className="grid grid-cols-2 gap-4">
            {/* Left Side */}
            <div className="space-y-4">
              {/* Payment Date */}
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <label className="block text-sm text-gray-600 mb-1">
                  Payment Date
                </label>
                <div className="flex gap-2">
                  <button
                    className={`px-4 py-2 rounded-full bg-purple-100 text-purple-600 text-sm ${
                      isSaved ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    onClick={handleNowButtonClick}
                    disabled={isSaved}
                  >
                    Now
                  </button>
                  <button
                    className={`px-4 py-2 rounded-full bg-gray-100 text-gray-600 text-sm flex items-center gap-2 ${
                      isSaved ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    onClick={toggleDatePicker}
                    disabled={isSaved}
                  >
                    Pick an older date
                    <Calendar className="h-4 w-4" />
                  </button>
                </div>

                {showDatePicker && (
                  <div className="mt-3">
                    <input
                      type="date"
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={transactionDate}
                      onChange={handleDateChange}
                      max={formattedCurrentDate}
                      disabled={isSaved}
                    />
                  </div>
                )}
              </div>

              {/* Amount and Payment Method */}
              <div className="bg-white p-4 rounded-lg space-y-4 shadow-sm">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    ₹ Amount
                  </label>
                  {/* <input
                    type="text"
                    className="w-full p-3 bg-white rounded-lg border border-gray-200"
                    placeholder="Enter amount"
                    value={amount}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || parseFloat(val) >= 0) {
                        setAmount(val);
                      }
                    }}
                    disabled={isSaved}
                  /> */}
                  <input
                    type="number"
                    min="0"
                    max={99_900_000_000}
                    className="w-full p-3 bg-white rounded-lg border border-gray-200"
                    placeholder="Enter amount"
                    value={amount}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "") {
                        setAmount("");
                        return;
                      }
                      const num = parseFloat(val);
                      if (num < 0) return;
                      if (num > 99_900_000_000) {
                        setAmount("99900000000");
                        return;
                      }
                      setAmount(val);
                    }}
                    disabled={isSaved}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Payment Method
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {["Cash", "UPI/Bank Transfer", "Cheque"].map((method) => (
                      <button
                        key={method}
                        className={`px-4 py-2 rounded-full text-sm ${
                          paymentMethod === method
                            ? "bg-purple-100 text-purple-600"
                            : "bg-gray-100 text-gray-600"
                        } ${isSaved ? "opacity-50 cursor-not-allowed" : ""}`}
                        onClick={() => setPaymentMethod(method)}
                        disabled={isSaved}
                      >
                        {method}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side */}
            <div className="space-y-4">
              {/* Remarks */}
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <input
                  type="text"
                  className="w-full p-2 bg-transparent border-b border-gray-300"
                  placeholder="Remarks / Comments (Optional)"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  disabled={isSaved}
                />
              </div>

              {/* Payment Slip */}
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <p className="text-sm text-gray-600 mb-2">
                  Payment Slip (Optional)
                </p>
                <div className="flex gap-3">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    id="image-upload"
                    onChange={handleImageUpload}
                    disabled={isSaved}
                  />
                  <label
                    htmlFor="image-upload"
                    className={`flex-1 p-3 bg-purple-50 text-purple-600 rounded-md text-center cursor-pointer ${
                      isSaved ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    Upload Photo
                  </label>
                </div>
                <div className="mt-4">
                  {previewUrl && (
                    <div className="relative w-full aspect-video">
                      <img
                        src={previewUrl}
                        alt="Receipt"
                        className="w-full h-full object-cover rounded-md"
                      />
                      {!isSaved && (
                        <button
                          onClick={removeImage}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <button
          className={`w-full mt-2 py-3 rounded-md font-medium transition duration-300 flex items-center justify-center gap-2 ${
            isSaveDisabled
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-[#3B0069] text-white hover:bg-purple-700"
          }`}
          onClick={handleSave}
          disabled={isSaveDisabled}
        >
          {isLoading ? (
            <>
              <Loader className="h-5 w-5 animate-spin" />
              Saving...
            </>
          ) : isSaved ? (
            "Saved"
          ) : (
            "Save"
          )}
        </button>
      </div>
    </div>
  );
}
